import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customersApi } from '../../api/endpoints/customers';
import {
  CustomerRegistrationRequest,
  CustomerUpdateRequest,
  CustomerListParams,
  BlockCustomerRequest,
} from '../../api/types/customer.types';

export const useCustomers = (params?: CustomerListParams) => {
  return useQuery({
    queryKey: ['customers', params],
    queryFn: () => customersApi.list(params),
  });
};

export const useCustomer = (customerId: string) => {
  return useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => customersApi.getById(customerId),
    enabled: !!customerId,
  });
};

export const useCustomerSearch = (query: string) => {
  return useQuery({
    queryKey: ['customers', 'search', query],
    queryFn: () => customersApi.search(query),
    enabled: query.length > 2,
  });
};

export const useRegisterCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CustomerRegistrationRequest) => customersApi.register(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
};

export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, data }: { customerId: string; data: CustomerUpdateRequest }) =>
      customersApi.update(customerId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customer', variables.customerId] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
};

export const useBlockCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, data }: { customerId: string; data: BlockCustomerRequest }) =>
      customersApi.block(customerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
};

export const useUnblockCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (customerId: string) => customersApi.unblock(customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
};

export const useCreditScore = (customerId: string) => {
  return useQuery({
    queryKey: ['creditScore', customerId],
    queryFn: () => customersApi.getCreditScore(customerId),
    enabled: !!customerId,
  });
};

export const useLoanLimit = (customerId: string) => {
  return useQuery({
    queryKey: ['loanLimit', customerId],
    queryFn: () => customersApi.getLoanLimit(customerId),
    enabled: !!customerId,
  });
};

// Aliases for consistency
export const useCustomerCreditScore = useCreditScore;
export const useCustomerLoanLimit = useLoanLimit;

export const useCustomerDocuments = (customerId: string) => {
  return useQuery({
    queryKey: ['customerDocuments', customerId],
    queryFn: () => customersApi.getDocuments(customerId),
    enabled: !!customerId,
  });
};

export const useVerifyKYC = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, data }: { customerId: string; data: import('../../api/types/customer.types').KYCVerificationRequest }) =>
      customersApi.verifyKYC(customerId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customer', variables.customerId] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customerDocuments', variables.customerId] });
    },
  });
};
